import { Alert, Grid, Link, Typography } from "@mui/material";
import { transformRotate, point, bbox, circle } from "@turf/turf";
import isOnline from "is-online";
import { useEffect, useRef, useState } from "react";
import { Layer, Source } from "react-map-gl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../AppRoutes";
import AppFooter from "../components/AppFooter";
import AppHeader from "../components/AppHeader";
import BodyContainer from "../components/BodyContainer";
import FooterButtons from "../components/FooterButtons";
import InfoField from "../components/InfoField";
import InfoFieldsContainer from "../components/InfoFieldsContainer";
import Map from "../components/Map";
import MapMarker from "../components/MapMarker";
import mapConfig from "../config/map.config";
import { smokeSensitiveAreasLayers } from "../config/map.constants";
import { layers, smokePlumeImg } from "../config/map.constants";
import { milesToFeet } from "../services/location.service";
import { getLargeSmokePlumePolygon, getSmallSmokePlumePolygon, getSmokePlumeRectangle } from "../services/map.service";
import { findNearestSSA, getCachedSSAs, getSSABasicInformationFromFeature, querySSAsInsidePolygon } from "../services/ssa.service";
import { store } from "../store";
import { setClosestSSA } from "../store/submission.slice";
import * as turf from '@turf/turf'

const SmokeSensitiveAreas = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	
	const location = useSelector(state => state.submission.location);
	const fuelLoad = useSelector(state => state.submission.fuelLoad);
	const weatherInfo = useSelector(state => state.submission.weather);

	const [map, setMap] = useState();
	const [mapCenter, setMapCenter] = useState();
	const [mapLayers, setMapLayers] = useState([]);
	const [sources, setSources] = useState([]);

	const [smokePlumeInfo, setSmokePlumeInfo] = useState({bounds: null, url: null, rotation: 0, centerX: 0});
	const [smokePlumePolygon, setSmokePlumePolygon] = useState();

	const [closestSSA, setClosestSSAa] = useState(null);
	const [noSSA, setNoSSA] = useState(false);
	const [ssaCloseMessage, setSsaCloseMessage] = useState(false);

	useEffect(() => {
		const isOnline = store.getState().cache.isOnline;
		if (isOnline) {
			setMapLayers(Object.values(layers).filter(l => l.id !== layers.arkansas_bound.id));
			setSources([
				layers.arkansas_highways.geojson,
				layers.arkansas_city_limits.geojson
			])
		} else {
			setSources([
				layers.arkansas_highways.geojson,
				layers.arkansas_city_limits.geojson,
				...getCachedSSAs()
			]);
		}
	}, [])

	useEffect(() => {
		if (!location) {
			navigate(appRoutes.SelectLocation);
		} else {
			const center = point([location.coords.longitude, location.coords.latitude]);
			const visibleExtent = bbox(circle(center, mapConfig.smokePlumeRadius, {units: 'miles'}));
			// setMapCenter({ ...location.coords, zoom: 9.5, bounds: visibleExtent });
			setMapCenter({ ...location.coords, zoom: 12, bounds: visibleExtent }); // 12 zoom is needed for queryFeatures to work on mapbox-gl
		}
	}, [location])


	useEffect(() => {
		let plumeInfo = { ...smokePlumeInfo };

		if (!plumeInfo.url && fuelLoad.totalAcres) {
			const plumeImg = fuelLoad.totalAcres >= 2000 ? smokePlumeImg.large : smokePlumeImg.small;
			plumeInfo =  {...plumeInfo, ...plumeImg };
		}

		// if ((!plumeInfo.coords || !plumeInfo.coords.length) && (!mapCenter|| !mapCenter.bounds) && weatherInfo.windDirection) {
		if ( mapCenter &&  mapCenter.bounds && weatherInfo.windDirection) {
			const windAngle = weatherInfo.windDirection ? (weatherInfo.windDirection.value || 0) : 0;
			let plumeImageBbox = getSmokePlumeRectangle(mapCenter.bounds, mapConfig.smokePlumeRadius, plumeInfo.rotation, plumeInfo.centerX);

			// Rotate image based on wind direction
			plumeImageBbox = transformRotate(plumeImageBbox, windAngle-90+180, {pivot: [location.coords.longitude,location.coords.latitude]})
			plumeInfo.bounds = plumeImageBbox.geometry.coordinates[0].slice(0, -1);
		}

		setSmokePlumeInfo(plumeInfo);
	}, [mapCenter, weatherInfo.windDirection, fuelLoad.totalAcres])

	useEffect(() => {
		if (!map || (!smokePlumeInfo || !smokePlumeInfo.name) || !mapCenter || !!smokePlumePolygon || !weatherInfo.windDirection) return;

		let plumePolygon = smokePlumeInfo.name === 'large' ?
		 getLargeSmokePlumePolygon([mapCenter.longitude, mapCenter.latitude], mapConfig.smokePlumeRadius) :
		 getSmallSmokePlumePolygon([mapCenter.longitude, mapCenter.latitude], mapConfig.smokePlumeRadius);

		const windAngle = weatherInfo.windDirection ? (weatherInfo.windDirection.value || 0) : 0;
		plumePolygon = transformRotate(plumePolygon, windAngle - 90 + 180, {pivot: [location.coords.longitude,location.coords.latitude]})

		setSmokePlumePolygon(plumePolygon);


	}, [map, smokePlumeInfo.name, mapCenter, weatherInfo.windDirection])

	const queried = useRef(false);
	const wait = async (ms) => {
		return new Promise((resolve) => setTimeout(() => resolve(), ms))
	}
	const findClosestSSA = async () => {
		if (!map || !smokePlumePolygon || queried.current) return;
		map.setZoom(9.5);
		await wait(1000);
		queried.current = true;

		const center = point([mapCenter.longitude, mapCenter.latitude]);
		const ssas = querySSAsInsidePolygon(map, smokePlumePolygon);
		
		let closestSsa = null, dist = -1, inPopulatedArea = false;
		ssas.forEach(ssa => {
			if (!ssa) return;
			const distMiles = findNearestSSA(center, ssa, smokePlumePolygon);
			if ((ssa.layer.id === layers.arkansas_city_limits.id) && turf.inside(center, ssa)) {
				inPopulatedArea = true
			}

			if (distMiles === undefined) return;

			if (distMiles < dist || dist == -1) {
				dist = distMiles;
				closestSsa = ssa;
			}
		})

		// If there is a city area within 1000 feet, default the distance to .1 (or less)
		dist = inPopulatedArea ? .1 : dist;

		const ssaInfo = closestSsa ? getSSABasicInformationFromFeature(closestSsa) : null;
		setClosestSSAa(ssaInfo);
		setNoSSA(!ssaInfo);
		setSsaCloseMessage(!!ssaInfo && (dist < 0.19));
		dispatch(setClosestSSA({distance: dist, name: ssaInfo}));
	}

	const onNext = () => {
		navigate(appRoutes.PlannedBurns);
	}

	const onMapLoaded = (map) => {
		setMap(map);
		const isOnline = store.getState().cache.isOnline;
		if (!isOnline) findClosestSSA();
	}

	const onSourcesLoaded = (sourcesIds) => {
		const ids = Array.from(sourcesIds);
		if (!map) return;
		const allSSALoaded = Object.values(smokeSensitiveAreasLayers).every(ssaLayer => {
			return ids.includes(ssaLayer.source) || !!map.getLayer(ssaLayer.id);
		});
		if (allSSALoaded) findClosestSSA();
	}

	return (
		<>
			<AppHeader />

			<BodyContainer hasFooter={false} hasHeader={true} sx>
				<Grid item xs="auto" mb={4} mt={2} sx={{width: '100% !important'}}>
					<Typography variant="title" component="h1">Identify Nearest Smoke-Sensitive Area</Typography>
					<Typography variant="subtitle2" component="h2">
						Keep smoke away from smoke-sensitive areas.
						These areas include Urban Centers (Green Polygon), 
						Hospitals (Red Square with White Cross), 
						Highways (Blue Lines), 
						Long Term Care Facilities (Aqua Square with White Wheel Chair), 
						Schools (Yellow Square with White Apple), and DEQ Monitoring Sites (Blue Square with White Beaker).
					</Typography>
				</Grid>
				<Grid item xs="auto" sx={{width: '100% !important'}}>
					{mapCenter &&
					<Map
					onMapLoad={onMapLoaded}
					onSourcesLoaded={onSourcesLoaded}
					mapLayers={mapLayers}
					sources={sources}
					bounds={mapCenter.bounds}
					dragRotate={false}
					touchPitch={false}
					navControls={true}
					touchZoomRotate={false}>
						{(smokePlumeInfo.url && smokePlumeInfo.bounds) &&
							<Source id="smoke-plume" type="image" url={smokePlumeInfo.url} coordinates={smokePlumeInfo.bounds}>
								<Layer id="smoke-plume-layer" source="plume-src" type="raster" paint={{"raster-opacity": 1, "raster-contrast": 1}} />
							</Source>
						}
						{smokePlumePolygon &&
							<Source id="smoke-plume-poly" type="geojson" data={smokePlumePolygon}>
								<Layer id="plume-layer" source="plume-poly-src" type="fill" paint={{
									'fill-color': "rgba(169,169,169, .3)",
									'fill-opacity': 1,
									'fill-outline-color': 'gray'
								}} />
							</Source>
						}

						{location && <MapMarker longitude={location.coords.longitude} latitude={location.coords.latitude} />}
					</Map>
					}
				</Grid>
				<Grid item xs="auto" mt={3} sx={{width: '100% !important'}}>
					<Typography variant="subtitle" component="h2" mb={2}>Nearest Smoke-Sensitive Area</Typography>
					<InfoFieldsContainer>
						{closestSSA ?
						<InfoField horizontal reverse title={`${closestSSA.name} - ${closestSSA.type}`}>
							{closestSSA.address}
						</InfoField>
						: 
						<InfoField horizontal reverse title='None'> </InfoField>
						}
					</InfoFieldsContainer>
					{ssaCloseMessage && 
						<Alert severity="warning" sx={{marginTop: '8px'}}>
							Recommend Do Not Burn - Please contact ADA dispatch center <Link href="tel:1-800-830-8015">(1-800-830-8015)</Link> if you have questions.
						</Alert>
					}
				</Grid>

				<Grid item xs="auto" mt={4} sx={{width: '100% !important'}}>
					<FooterButtons backLabel="Go Back" onNext={onNext} onBack={() => navigate(appRoutes.FuelLoading)} nextDisabled={!closestSSA && !noSSA} />
				</Grid>
			</BodyContainer>

			{/* <AppFooter /> */}
		</>
	)
}

export default SmokeSensitiveAreas;